import { createClient } from "@connectrpc/connect";
import { grpcTransportProvider } from "../grpc/GrpcTransport";
import { mapConnectError } from "../grpc/mapConnectError";
import { StorageService as StorageGrpcService } from "../../../generated/grpc/storage_pb";
import { SasReadUrlResponse } from "../../response/storage/SasReadUrlResponse";

/** Margen de seguridad: refrescamos el SAS 30 min antes de que expire,
 *  para no usar un token que caduque a mitad de carga de tiles. */
const EXPIRY_MARGIN_MS = 30 * 60_000;

/** Obtiene el SAS de lectura (24h) de los tiles. El token es a nivel de
 *  CUENTA de storage (Account SAS: ss=b, srt=co, sp=r) — sirve para leer
 *  blobs de cualquier contenedor (ortomosaico, conteo, índices satelitales),
 *  no solo el del pre-render que lo originó. Se cachea una sola vez por
 *  sesión y se reutiliza hasta que esté por expirar. Se pide por gRPC-Web
 *  (`StorageService.GetTilesSasRead` — antes `GET storage/tiles/sas-read`,
 *  ver CLAUDE.md del Server, sección migración a gRPC-Web). */
class StorageTilesSasReadService {
  /** SAS cacheado en memoria (o null si aún no se pidió). */
  private _cached: SasReadUrlResponse | null = null;
  /** Petición en curso, para deduplicar llamadas concurrentes. */
  private _inFlight: Promise<SasReadUrlResponse> | null = null;

  /** Retorna el SAS de lectura: usa la caché si es válida, reutiliza la petición
   *  en vuelo si la hay, o pide uno nuevo y lo cachea. */
  async getAsync(): Promise<SasReadUrlResponse> {
    // 1) Caché válida → devolver sin tocar la red.
    if (this._cached && !this._isExpiring(this._cached)) {
      return this._cached;
    }

    // 2) Ya hay una petición en curso → reutilizarla (evita fetch duplicado
    //    si dos toggles encienden el conteo casi al mismo tiempo).
    if (this._inFlight) return this._inFlight;

    // 3) Pedir uno nuevo.
    const client = createClient(StorageGrpcService, grpcTransportProvider.get());
    this._inFlight = client
      .getTilesSasRead({})
      .then((response) => {
        const res: SasReadUrlResponse = {
          sasToken: response.sasToken,
          expiresOn: response.expiresOn,
        };
        this._cached = res;
        return res;
      })
      .catch((error) => {
        throw mapConnectError(error);
      })
      .finally(() => {
        this._inFlight = null;
      });

    return this._inFlight;
  }

  /** `true` si el SAS expira dentro del margen de seguridad (hay que refrescarlo). */
  private _isExpiring(sas: SasReadUrlResponse): boolean {
    return new Date(sas.expiresOn).getTime() - Date.now() <= EXPIRY_MARGIN_MS;
  }
}

/** Singleton del proveedor de SAS de lectura de tiles. */
export const storageTilesSasReadService = new StorageTilesSasReadService();
